import { paywallFooterElement, revcatOfferingId, stripeConfig, stripeConfigItem } from '@inkitt/types-common';
import { z } from 'zod';

export const gtvIntroOfferPayload = z.object({
  revcatOfferingId,
  subscriptionOptionId: z.string().optional(),
  minimumPaywallSeenCount: z.number().optional(),
  offerDurationMs: z.number().optional(),
  betweenPaywallAndPopupMillis: z.number().optional(),
});
export type GtvIntroOfferPayload = z.infer<typeof gtvIntroOfferPayload>;

export const gtvPricingTestPayload = z.object({
  revcatOfferingId,
  pointsFooterElement: paywallFooterElement.optional(),
  stripeSettings: z.object({ config: z.record(z.string(), stripeConfigItem).optional() }).optional(),
  pricingType: z.enum(['compact', 'wide']).optional(),
  revcatCoinsOfferingId: revcatOfferingId.optional(),
  showStickerIfHighlighted: z.boolean().optional(),
  highlightedCoinsOfferingId: z.string().optional(),
  /** @deprecated */
  buttons: z.array(z.object({ type: z.enum(['individual', 'family']) })).optional(),
  introOfferPayload: gtvIntroOfferPayload.optional(),
});
export type GtvPricingTestPayload = z.infer<typeof gtvPricingTestPayload>;

export const gtvStripeConfig = stripeConfig.extend({
  stripeWebViewEnabled: z.boolean().optional(),
});

export const gtvPricingTestVariants = z.enum([
  'control',
  'default',
  'apple_review',
  'google_review',
  'with_fake_feature',
  'candy_crush_strategy',
  // one A/B per country purchasing-power tier (t1, t2, t3_4),
  // with control vs treatment vs aa_variant arms. The aa_variant arm is
  // A/A instrumentation — identical to control, used to measure baseline
  // noise. All nine are children of the testPricingV3 strategy.
  'geo_pricing_t1_control',
  'geo_pricing_t1_aa_variant',
  'geo_pricing_t1_treatment',
  'geo_pricing_t2_control',
  'geo_pricing_t2_aa_variant',
  'geo_pricing_t2_treatment',
  'geo_pricing_t3_4_control',
  'geo_pricing_t3_4_aa_variant',
  'geo_pricing_t3_4_treatment',
  'yearly_69_99_control',
  'yearly_69_99_aa_variant',
  'yearly_99_99',
  'yearly_199_99',
  // Weekly / Monthly / Yearly acquisition offering. Presentation comes from the
  // offering metadata, never from these names.
  'monthly_control',
  'monthly_aa_variant',
  'monthly_treatment',
]);
export type GtvPricingTestVariants = z.infer<typeof gtvPricingTestVariants>;

export const gtvPricingTestDefinition = {
  definition: gtvPricingTestPayload.optional(),
  variants: gtvPricingTestVariants,
};

/** Roku sells subscriptions only, so the arm needs nothing but the offering to show. */
export const rokuPricingTestPayload = z.object({
  revcatOfferingId,
});
export type RokuPricingTestPayload = z.infer<typeof rokuPricingTestPayload>;

export const rokuPricingTestVariants = z.enum(['control', 'variant_yearly']);

export const rokuPricingTestDefinition = {
  definition: rokuPricingTestPayload,
  variants: rokuPricingTestVariants,
};
